import React, { useState, useEffect } from 'react';
import {
    Clock,
    Calendar,
    AlertCircle,
    X,
    PlusCircle,
    MessageSquare,
    Edit,
    User,
    ArrowLeft,
} from 'lucide-react';
import api from '../services/api';

const UnsavedChangesModal = ({ onYes, onNo }) => (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900">Unsaved Changes</h2>
            <p className="mt-2 text-gray-600">You have unsaved changes. Would you like to save them before proceeding?</p>
            <div className="mt-6 flex justify-end space-x-3">
                <button
                    onClick={onNo}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                    Don't Save
                </button>
                <button
                    onClick={onYes}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                    Save Changes
                </button>
            </div>
        </div>
    </div>
);

//  Section Helper Component 
const Section = ({ title, children }) => (
    <div className="border rounded-lg bg-white shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 px-4 py-3 border-b bg-gray-50 rounded-t-lg">{title}</h3>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {children}
        </div>
    </div>
);

//  CommentSection Component 
const CommentSection = ({ comments, recordId, onCommentAdded, user, api }) => {
    const [showCommentForm, setShowCommentForm] = useState(false);
    const [comment, setComment] = useState('');
    const [status, setStatus] = useState('');
    const [scheduledDate, setScheduledDate] = useState('');
    const [scheduledTime, setScheduledTime] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [timeSlots, setTimeSlots] = useState([]);

    // MODIFIED: Only payment_received should NOT show the calendar. 
    const statusesWithoutEvent = ['payment_received'];
    const showScheduleFields = status && !statusesWithoutEvent.includes(status);

    const handleStatusChange = (e) => {
        const newStatus = e.target.value;
        setStatus(newStatus);
        if (statusesWithoutEvent.includes(newStatus)) {
            setScheduledDate('');
            setScheduledTime('');
        }
    };

    const generateTimeSlots = (selectedDate) => {
        const slots = [];
        if (!selectedDate) return slots;
        const now = new Date();
        const [year, month, day] = selectedDate.split('-').map(Number);
        const inputDate = new Date(year, month - 1, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isToday = inputDate.getTime() === today.getTime();
        let startHour = 0;
        let startMinute = 0;
        if (isToday) {
            startHour = now.getHours();
            let roundedMinute = Math.ceil((now.getMinutes() + 1) / 15) * 15;
            if (roundedMinute >= 60) {
                startHour += 1;
                startMinute = 0;
            } else {
                startMinute = roundedMinute;
            }
            if (startHour >= 24) return [];
        }
        for (let hour = startHour; hour < 24; hour++) {
            const minuteStart = (hour === startHour) ? startMinute : 0;
            for (let minute = minuteStart; minute < 60; minute += 15) {
                const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                slots.push(timeStr);
            }
        }
        return slots;
    };

    useEffect(() => {
        const newTimeSlots = generateTimeSlots(scheduledDate);
        setTimeSlots(newTimeSlots);
        if (scheduledDate && scheduledTime && !newTimeSlots.includes(scheduledTime)) {
            setScheduledTime('');
        }
    }, [scheduledDate, scheduledTime]);

    // MODIFIED: Updated list to include all fields from records details
    const statusOptions = [
        { value: 'callback', label: 'Call Back' },
        { value: 'lvm', label: 'Left Voice Message' },
        { value: 'spoke_to', label: 'Spoke To' },
        { value: 'sent_email_fax', label: 'Sent Email/Fax' },
        { value: 'received_email_fax', label: 'Recieved Email/Fax' },
        { value: 'offer', label: 'Offer' },
        { value: 'settle', label: 'Settle' },
        { value: 'request_to_close', label: 'Request to Close' },
        { value: 'previously settled', label: 'Previously Settled' },
        { value: 'out of sol', label: 'Out Of Sol' },
        { value: 'dor filed', label: 'Dor Filed' },
        { value: 'wfp', label: 'Waiting for Payment' },
        { value: 'file_pmr', label: 'File PMR' },
        { value: 'file_lien', label: 'File Lien' },
        { value: 'payment_received', label: 'Payment Received' },
        { value: 'hearing_remarks', label: 'Hearing Remarks' },
        { value: 'closed', label: 'Closed' },
        ...(user?.role === 'admin' ? [{ value: 'closed', label: 'Closed' }] : [])
    ];

    // MODIFIED: Updated badges to match the expanded list
    const getStatusBadge = (statusValue) => ({
        'callback': { label: 'Call Back', color: 'bg-blue-100 text-blue-800' },
        'lvm': { label: 'Left Voice Message', color: 'bg-yellow-100 text-yellow-800' },
        'spoke_to': { label: 'Spoke To', color: 'bg-green-100 text-green-800' },
        'sent_email_fax': { label: 'Sent Email/Fax', color: 'bg-purple-100 text-purple-800' },
        'received_email_fax': { label: 'Received Email/Fax', color: 'bg-pink-100 text-pink-800', },
        'offer': { label: 'Offer', color: 'bg-indigo-100 text-indigo-800' },
        'settle': { label: 'Settle', color: 'bg-pink-100 text-pink-800' },
        'request_to_close': { label: 'Request to Close', color: 'bg-teal-100 text-teal-800' },
        'previously settled': { label: 'Previously Settled', color: 'bg-gray-100 text-gray-800' },
        'out of sol': { label: 'Out Of Sol', color: 'bg-gray-100 text-gray-800' },
        'dor filed': { label: 'Dor Filed', color: 'bg-cyan-100 text-cyan-800' },
        'wfp': { label: 'Waiting for Payment', color: 'bg-orange-100 text-orange-800' },
        'file_pmr': { label: 'File PMR', color: 'bg-violet-100 text-violet-800' },
        'file_lien': { label: 'File Lien', color: 'bg-rose-100 text-rose-800' },
        'payment_received': { label: 'Payment Received', color: 'bg-emerald-100 text-emerald-800' },
        'hearing_remarks': { label: 'Hearing Remarks', color: 'bg-lime-100 text-lime-800' },
        'closed': { label: 'Closed', color: 'bg-red-100 text-red-800' },
    }[statusValue] || { label: statusValue, color: 'bg-gray-100 text-gray-800' });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!comment.trim() || !status) {
            setError('Comment and Status fields are required.');
            return;
        }
        if (showScheduleFields && (!scheduledDate || !scheduledTime)) {
            setError('Follow-up Date and Time are required for this status.');
            return;
        }
        setIsSubmitting(true);
        try {
            const commentData = { text: comment, status: status };
            if (showScheduleFields) {
                commentData.scheduledDate = scheduledDate;
                commentData.scheduledTime = scheduledTime;
            }
            await onCommentAdded(recordId, commentData);
            setComment('');
            setStatus('');
            setScheduledDate('');
            setScheduledTime('');
            setShowCommentForm(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add comment. Please try again.');
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const today = `${year}-${month}-${day}`;

    return (
        <div className="border rounded-lg bg-white shadow-sm">
            <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-50 rounded-t-lg">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2"><MessageSquare className="w-5 h-5" />Comments & Activity Log</h3>
                <button onClick={() => setShowCommentForm(s => !s)} className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg text-sm flex items-center gap-2">
                    {showCommentForm ? <X className="w-4 h-4" /> : <PlusCircle className="w-4 h-4" />}
                    {showCommentForm ? 'Cancel' : 'Add Comment'}
                </button>
            </div>

            {showCommentForm && (
                <div className="p-4 bg-gray-50 border-b">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Comment *</label>
                            <textarea rows={3} className="w-full p-2 border border-gray-300 rounded-md" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Enter your comment..." required />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                                <select className="w-full p-2 border border-gray-300 rounded-md" value={status} onChange={handleStatusChange} required>
                                    <option value="">Select status</option>
                                    {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                </select>
                            </div>
                            {showScheduleFields && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Date *</label>
                                        <input type="date" className="w-full p-2 border border-gray-300 rounded-md" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} min={today} required={showScheduleFields} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Time *</label>
                                        <select className="w-full p-2 border border-gray-300 rounded-md" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} disabled={!scheduledDate} required={showScheduleFields}>
                                            <option value="">Select time</option>
                                            {timeSlots.map(slot => <option key={slot} value={slot}>{slot}</option>)}
                                        </select>
                                    </div>
                                </>
                            )}
                        </div>
                        {error && <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{error}</div>}
                        <div className="flex justify-end space-x-3">
                            <button type="button" onClick={() => setShowCommentForm(false)} className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md">Cancel</button>
                            <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md disabled:bg-gray-400">{isSubmitting ? 'Adding...' : 'Add Comment'}</button>
                        </div>
                    </form>
                </div>
            )}

            <div className="p-4">
                {comments && comments.length > 0 ? (
                    <div className="space-y-4">
                        {comments.map((c) => {
                            const badge = getStatusBadge(c.status);
                            let followUpDisplay = null;
                            if (c.scheduledDate) {
                                const isCompleted = c.isCompleted;
                                let isPastDue = false;
                                const eventDateObj = new Date(c.scheduledDate);
                                const eventDate = new Date(eventDateObj.getUTCFullYear(), eventDateObj.getUTCMonth(), eventDateObj.getUTCDate());
                                const now = new Date();
                                if (c.scheduledTime) {
                                    const [hours, minutes] = c.scheduledTime.split(':').map(Number);
                                    const eventEndTime = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate(), hours, minutes + 15);
                                    isPastDue = !isCompleted && now > eventEndTime;
                                } else {
                                    const endOfDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate(), 23, 59, 59);
                                    isPastDue = !isCompleted && now > endOfDay;
                                }
                                let bgColor = 'bg-blue-50 text-blue-700';
                                let statusText = null;
                                if (isCompleted) {
                                    bgColor = 'bg-green-50 text-green-700';
                                    statusText = <span className="font-bold ml-auto">(Completed)</span>;
                                } else if (isPastDue) {
                                    bgColor = 'bg-red-50 text-red-700';
                                    statusText = <span className="font-bold ml-auto">(Overdue)</span>;
                                }
                                followUpDisplay = (
                                    <div className={`text-xs p-2 rounded w-full flex items-center gap-2 ${bgColor}`}>
                                        <Calendar className="w-4 h-4" />
                                        <span>Follow-up: {eventDate.toLocaleDateString()} {c.scheduledTime && ` at ${c.scheduledTime}`}</span>
                                        {statusText}
                                    </div>
                                );
                            }
                            return (
                                <div key={c._id} className="border rounded-lg p-3 bg-gray-50">
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>{badge.label}</span>
                                            {followUpDisplay}
                                        </div>
                                        <span className="text-xs text-gray-500">
                                            {c.createdAt ? new Date(c.createdAt).toLocaleString() : ''}
                                            <span className="ml-1 text-gray-400">|</span>
                                            <span className="ml-1 font-medium text-gray-600">
                                                Commented By: {c.author?.username || c.createdBy || 'Unknown'}
                                            </span>
                                        </span>
                                    </div>

                                    <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                        {c.text}
                                    </p>

                                    {c.createdBy && (
                                        <p className="text-xs text-gray-500">
                                            By: {c.createdBy}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : <p className="text-gray-500 text-sm text-center py-4">No comments yet</p>}
            </div>
        </div>
    );
};


//  Main HearingRecordDetails Page Component 
const HearingRecordDetails = ({ recordId, onBack }) => {

    //  State for Page View 
    const [selectedRecordData, setSelectedRecordData] = useState(null);
    const [recordLoading, setRecordLoading] = useState(true);
    const [recordError, setRecordError] = useState(null);

    //  State for Form Editing
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [initialEditData, setInitialEditData] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [modalDiscardAction, setModalDiscardAction] = useState(null);

    //  State for Form Dropdowns 
    const [user, setUser] = useState(null);

    // Helper functions
    const deepCopy = (data) => JSON.parse(JSON.stringify(data));

    //  Fetch Full Record Data 
    useEffect(() => {
        if (!recordId) return;

        const fetchRecord = async () => {
            setRecordLoading(true);
            setRecordError(null);
            try {
                const response = await api.get(`/records/${recordId}`);
                setSelectedRecordData(response.data);

                // Set initial form data once record is fetched
                const initialData = {};
                const record = response.data;
                Object.keys(record).forEach(key => {
                    if (['hearingDate', 'caseDate', 'dob', 'fds', 'lds', 'solDate'].includes(key) && record[key]) {
                        try {
                            initialData[key] = new Date(record[key]).toISOString().split('T')[0];
                        } catch (e) { initialData[key] = record[key]; }
                    } else {
                        if (Array.isArray(record[key])) {
                            initialData[key] = record[key].map(item => ({ ...item }));
                        } else {
                            initialData[key] = record[key];
                        }
                    }
                });
                setEditData(initialData);
                setInitialEditData(deepCopy(initialData));

            } catch (err) {
                setRecordError('Failed to fetch hearing details. Please try again.');
                console.error(err);
            } finally {
                setRecordLoading(false);
            }
        };

        fetchRecord();
    }, [recordId]);


    //  Fetch Form Data (User) 
    useEffect(() => {
        const fetchFormData = async () => {
            try {
                const userRes = await api.get('/users/me');
                setUser(userRes.data);
            } catch (err) {
                console.error("Failed to fetch user", err);
            }
        };
        fetchFormData();
    }, []);


    // Form Logic 

    // This useEffect populates the form when the fetched data changes
    useEffect(() => {
        if (!selectedRecordData) {
            setEditData({});
            setInitialEditData({});
            return;
        }

        const initialData = {};
        Object.keys(selectedRecordData).forEach(key => {
            if (['hearingDate', 'caseDate', 'dob', 'fds', 'lds', 'solDate'].includes(key) && selectedRecordData[key]) {
                try {
                    initialData[key] = new Date(selectedRecordData[key]).toISOString().split('T')[0];
                } catch (e) { initialData[key] = selectedRecordData[key]; }
            } else {
                if (Array.isArray(selectedRecordData[key])) {
                    initialData[key] = selectedRecordData[key].map(item => ({ ...item }));
                } else {
                    initialData[key] = selectedRecordData[key];
                }
            }
        });
        setEditData(initialData);
        setInitialEditData(deepCopy(initialData));
        setStatusMessage({ type: '', text: '' });
    }, [selectedRecordData]);

    const caseStatusOptions = [
        { value: '', label: 'Select status ' },
        { value: 'SETTLED', label: 'SETTLED' },
        { value: 'C & R (GRANTED)', label: 'C & R (GRANTED)' },
        { value: 'CIC PENDING', label: 'CIC PENDING' },
        { value: 'A & S GRANTED', label: 'A & S GRANTED' },
        { value: 'ADR CASE - SETTED AND PAID ADR', label: 'ADR CASE - SETTED AND PAID ADR' },
        { value: 'ORDER OF DISMISAAL OF CASE', label: 'ORDER OF DISMISAAL OF CASE' },
    ];

    const hasUnsavedChanges = () => JSON.stringify(editData) !== JSON.stringify(initialEditData);

    const resetEditData = () => {
        setEditData(deepCopy(initialEditData));
    };

    const handleEditChange = (e) => setEditData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSave = async () => {
        setIsSaving(true);
        setStatusMessage({ type: '', text: '' });

        const cleanedEditData = { ...editData };
        ['claimNo', 'adjNumber', 'doi'].forEach(field => {
            if (Array.isArray(cleanedEditData[field])) {
                cleanedEditData[field] = cleanedEditData[field].filter(item => item.value && item.value.trim() !== '');
            }
        });

        try {
            setEditData(cleanedEditData);
            const response = await api.put(`/records/${selectedRecordData._id}`, cleanedEditData);
            const updatedRecord = response.data;
            setSelectedRecordData(updatedRecord); // This will trigger the useEffect to reset form data
            setIsEditing(false);
            setStatusMessage({ type: 'success', text: 'Record saved successfully!' });
        } catch (error) {
            setStatusMessage({ type: 'error', text: 'Failed to save record.' });
        } finally {
            setIsSaving(false);
            setTimeout(() => setStatusMessage({ type: '', text: '' }), 4000);
        }
    };

    const handleCancelEditClick = () => {
        if (isEditing && hasUnsavedChanges()) {
            setModalDiscardAction('cancelEdit');
            setShowUnsavedModal(true);
        } else {
            setIsEditing(false);
            resetEditData();
        }
    };

    const handleAddComment = async (recordId, commentData) => {
        try {
            const response = await api.post(`/records/${recordId}/comments`, commentData);
            setSelectedRecordData(response.data); // Update record with new comment
            return response.data;
        } catch (err) {
            console.error("Failed to add comment", err);
            throw err;
        }
    };

    const handleMultiEntryChange = (fieldName, index, value) => {
        setEditData(prev => {
            const newItems = prev[fieldName] ? [...prev[fieldName]] : [];
            if (newItems[index]) {
                newItems[index] = { ...newItems[index], value: value };
            } else {
                newItems[index] = { value: value };
            }
            return { ...prev, [fieldName]: newItems };
        });
    };

    const addMultiEntry = (fieldName) => {
        setEditData(prev => {
            const newItems = prev[fieldName] ? [...prev[fieldName]] : [];
            newItems.push({ value: '' });
            return { ...prev, [fieldName]: newItems };
        });
    };

    const removeMultiEntry = (fieldName, index) => {
        setEditData(prev => {
            const newItems = [...(prev[fieldName] || [])];
            newItems.splice(index, 1);
            return { ...prev, [fieldName]: newItems };
        });
    };

    const patientInfoFields = ['provider', 'renderingFacility', 'taxId', 'ptName', 'dob', 'ssn', 'employer', 'solDate', 'adjNumber', 'doi'];
    const billingInfoFields = ['bill', 'paid', 'outstanding', 'ledger', 'hcf', 'invoice', 'signinSheet', 'fds', 'lds'];
    const insuranceDefenseFields = ['insurance', 'claimNo', 'adjuster', 'adjusterPhone', 'adjusterFax', 'adjusterEmail', 'defenseAttorney', 'defenseAttorneyPhone', 'defenseAttorneyFax', 'defenseAttorneyEmail'];
    //  MODIFIED: Added new fields 
    const caseHearingFields = ['hearingStatus', 'hearingDate', 'hearingTime', 'judgeName', 'courtRoomlink', 'judgePhone', 'AccesCode', 'boardLocation', 'lienStatus', 'caseStatus', 'caseDate', 'crAmount', 'dorFiledBy', 'status4903_8', 'pmrStatus', 'judgeOrderStatus'];

    const renderField = (field) => {
        const amountFields = ['bill', 'paid', 'outstanding', 'crAmount'];
        const multiFields = ['adjNumber', 'doi', 'claimNo'];
        const data = editData; // Use editData for consistent view/edit source

        if (multiFields.includes(field)) {
            const items = data[field] || [];
            return (
                <div key={field} className="space-y-1 col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-4">
                    <label className="block text-xs font-medium text-gray-500 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                    <div className="p-2 bg-gray-50 rounded border min-h-[40px]">
                        {items && items.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3">
                                {items.map((item, index) => (
                                    <div key={index} className="p-2 bg-gray-100 rounded text-center font-mono text-xs break-all">{item.value}</div>
                                ))}
                            </div>
                        ) : <p className="text-gray-500 text-sm">N/A</p>}
                    </div>
                </div>
            );
        }

        let displayValue = data[field];

        if (amountFields.includes(field)) {
            const value = parseFloat(displayValue);
            displayValue = !isNaN(value)
                ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
                : 'N/A';
        }
        else if (['dob', 'fds', 'lds', 'solDate', 'hearingDate', 'caseDate'].includes(field) && data[field]) {
            try {
                if (typeof data[field] === 'string' && data[field].match(/^\d{4}-\d{2}-\d{2}$/)) {
                    const date = new Date(data[field] + 'T00:00:00');
                    displayValue = date.toLocaleDateString();
                } else {
                    displayValue = new Date(data[field]).toLocaleDateString(undefined, {
                        timeZone: 'UTC',
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric'
                    });
                }
            } catch (e) {
                displayValue = data[field];
            }
        }

        return (
            <div key={field} className="space-y-1">
                <label className="block text-xs font-medium text-gray-500 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                <p className="text-sm p-2 bg-gray-50 rounded border min-h-[40px] break-words">{displayValue || 'N/A'}</p>
            </div>
        );
    };

    const renderEditableField = (field) => {
        const amountFields = ['bill', 'paid', 'outstanding', 'crAmount'];
        const dateFields = ['hearingDate', 'caseDate', 'dob', 'fds', 'lds', 'solDate'];
        const multiFields = ['adjNumber', 'doi', 'claimNo'];
        const dropdownFields = ['ledger', 'hcf', 'invoice', 'signinSheet'];
        const caseStatusField = 'caseStatus';
        const lienStatusField = 'lienStatus';

        //  NEW: Options for the requested dropdowns 
        const customDropdownOptions = {
            'dorFiledBy': [
                { value: '', label: 'Select Filer ' },
                { value: 'HUBUR', label: 'HUBUR' },
                { value: 'CLIENT', label: 'CLIENT' },
                { value: 'ANOTHER LIEN CLAIMANT', label: 'ANOTHER LIEN CLAIMANT' }
            ],
            'status4903_8': [
                { value: '', label: 'Select Status' },
                { value: 'yes', label: 'yes' },
                { value: 'no', label: 'no' }
            ],
            'pmrStatus': [
                { value: '', label: 'Select Status' },
                { value: 'yes', label: 'yes' },
                { value: 'no', label: 'no' }
            ],
            'judgeOrderStatus': [
                { value: '', label: 'Select Status ' },
                { value: 'GRANTED', label: 'GRANTED' },
                { value: 'PENDING', label: 'PENDING' }
            ],
            //  ADDED HERE: 
            'hearingTime': [
                { value: '', label: 'Select Time' },
                { value: 'AM', label: 'AM' },
                { value: 'PM', label: 'PM' }
            ]
        };

        // NEW: Options for Lien Status dropdown
        const lienStatusOptions = [
            { value: '', label: 'Select status (default)' },
            { value: 'Not Filed', label: 'Not Filed' },
            { value: 'Out of SOL', label: 'Out of SOL' },
            { value: 'Filed', label: 'Filed' },
            { value: 'Settled', label: 'Settled' },
        ];

        if (multiFields.includes(field)) {
            const items = editData[field] || [];
            return (
                <div key={field} className="space-y-2 col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-4">
                    <label className="block text-xs font-medium text-gray-700 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                    {items.map((item, index) => (
                        <div key={index} className="flex items-center gap-2">
                            <input
                                type="text"
                                name={`${field}-${index}`}
                                value={item.value || ''}
                                onChange={(e) => handleMultiEntryChange(field, index, e.target.value)}
                                className="block w-full p-2 text-sm bg-white border border-gray-300 rounded"
                            />
                            <button type="button" onClick={() => removeMultiEntry(field, index)} className="text-red-500 hover:text-red-700 flex-shrink-0">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={() => addMultiEntry(field)} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1">
                        <PlusCircle className="w-4 h-4" /> Add {field.replace(/([A-Z])/g, ' $1')}
                    </button>
                </div>
            );
        }

        //  NEW: Handle the new custom dropdowns 
        if (customDropdownOptions[field]) {
            return (
                <div key={field} className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                    <select
                        name={field}
                        value={editData[field] || ''}
                        onChange={handleEditChange}
                        className="block w-full p-2 text-sm bg-white border border-gray-300 rounded"
                    >
                        {customDropdownOptions[field].map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            );
        }

        if (dropdownFields.includes(field)) {
            return (
                <div key={field} className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                    <select
                        name={field}
                        value={editData[field] || 'not required'}
                        onChange={handleEditChange}
                        className="block w-full p-2 text-sm bg-white border border-gray-300 rounded"
                    >
                        <option value="not required">Not Required</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                    </select>
                </div>
            );
        }

        if (field === caseStatusField) {
            return (
                <div key={field} className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                    <select
                        name={field}
                        value={editData[field] || ''}
                        onChange={handleEditChange}
                        className="block w-full p-2 text-sm bg-white border border-gray-300 rounded"
                    >
                        {caseStatusOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            );
        }

        if (field === lienStatusField) {
            return (
                <div key={field} className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                    <select
                        name={field}
                        value={editData[field] || ''}
                        onChange={handleEditChange}
                        className="block w-full p-2 text-sm bg-white border border-gray-300 rounded"
                    >
                        {lienStatusOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            );
        }

        const isAmount = amountFields.includes(field);
        const isDate = dateFields.includes(field);
        const inputType = isDate ? 'date' : (isAmount ? 'number' : 'text');

        return (
            <div key={field} className="space-y-1">
                <label className="block text-xs font-medium text-gray-700 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                <input
                    type={inputType}
                    name={field}
                    value={editData[field] || ''}
                    onChange={handleEditChange}
                    className="block w-full p-2 text-sm bg-white border border-gray-300 rounded"
                    {...(isAmount ? { step: '0.01' } : {})}
                />
            </div>
        );
    };

    // Page Handlers 
    const handleBackClick = () => {
        if (isEditing && hasUnsavedChanges()) {
            setModalDiscardAction('back');
            setShowUnsavedModal(true);
        } else {
            onBack(); // Call the prop to go back
        }
    };

    const handleUnsavedYes = async () => {
        // "Yes" always saves, stays on the page, and exits edit mode
        await handleSave();
        setShowUnsavedModal(false);
        setModalDiscardAction(null);
    };

    const handleUnsavedNo = () => {
        // "No" discards and performs the tracked action
        setShowUnsavedModal(false);
        resetEditData();
        setIsEditing(false);

        if (modalDiscardAction === 'back') {
            onBack(); // Go back
        }
        // if 'cancelEdit', it just stops editing (which it now has)

        setModalDiscardAction(null);
    };


    return (
        <div className="h-full flex flex-col bg-gray-100">
            {/* HEADER (from RecordDetails) */}
            <header className="flex-shrink-0 bg-white border-b shadow-sm sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <div className="flex-1 min-w-0">
                            {/* Adapted to use component's state */}
                            <h2 className="text-2xl font-bold text-gray-800 truncate">
                                {recordLoading ? 'Loading...' : `Record: ${selectedRecordData?.ptName || 'Details'}`}
                            </h2>
                            {!recordLoading && selectedRecordData && (
                                <div className="mt-1 flex flex-wrap gap-x-4 text-sm text-gray-600">
                                    {/* --- ADDED REF ID HERE --- */}
                                    <span className="bg-gray-100 px-2 py-0.5 rounded border border-gray-200 flex items-center gap-1">
                                        <b>Ref ID:</b> {selectedRecordData.referenceId || 'N/A'}
                                    </span>
                                    {/* ------------------------- */}
                                    <span><b>Status:</b> {selectedRecordData.caseStatus || 'N/A'}</span>
                                    <span><b>Collector:</b> {selectedRecordData.assignedCollector?.username || 'Unassigned'}</span>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-3 ml-4">
                            {/* Logic wired to component's state and handlers */}
                            {!recordLoading && selectedRecordData && (
                                <>
                                    {!isEditing ? (
                                        <button onClick={() => setIsEditing(true)} className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg text-sm flex items-center gap-1"><Edit className="w-4 h-4" /> Edit</button>
                                    ) : (
                                        <>
                                            <button onClick={handleSave} disabled={isSaving} className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg disabled:bg-gray-400 text-sm">{isSaving ? 'Saving...' : 'Save'}</button>
                                            <button onClick={handleCancelEditClick} className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-lg text-sm">Cancel</button>
                                        </>
                                    )}
                                </>
                            )}
                            <button onClick={handleBackClick} className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-lg text-sm flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>
                        </div>
                    </div>
                </div>
                {statusMessage.text && <div className={`px-4 py-2 text-center text-sm ${statusMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{statusMessage.text}</div>}
            </header>

            {/* NEW MAIN CONTENT (from RecordDetails) */}
            <main className="flex-1 overflow-y-auto">
                {recordLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
                        <p className="ml-4 text-gray-600">Loading record details...</p>
                    </div>
                ) : recordError ? (
                    <div className="max-w-7xl mx-auto p-4 sm:px-6 lg:px-8">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                            <p className="text-red-800">{recordError}</p>
                            <button onClick={handleBackClick} className="ml-auto text-sm font-medium text-red-700 hover:underline">Back</button>
                        </div>
                    </div>
                ) : (
                    <div className="max-w-7xl mx-auto p-4 sm:px-6 lg:px-8 space-y-6">

                        <Section title="Patient Information">
                            {isEditing ? patientInfoFields.map(renderEditableField) : patientInfoFields.map(renderField)}
                        </Section>

                        <Section title="Billing Information">
                            {isEditing ? billingInfoFields.map(renderEditableField) : billingInfoFields.map(renderField)}
                        </Section>

                        <Section title="Insurance & Defense Information">
                            {isEditing ? insuranceDefenseFields.map(renderEditableField) : insuranceDefenseFields.map(renderField)}
                        </Section>

                        <Section title="Case & Hearing (Eams Status)">
                            {isEditing ? caseHearingFields.map(renderEditableField) : caseHearingFields.map(renderField)}
                        </Section>

                        <CommentSection
                            comments={[...(selectedRecordData.comments || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
                            recordId={selectedRecordData._id}
                            onCommentAdded={handleAddComment}
                            user={user}
                            api={api} // Pass the api instance to the comment section
                        />
                    </div>
                )}
            </main>

            {/* Unsaved Changes Modal (using RecordDetails logic) */}
            {showUnsavedModal && (
                <UnsavedChangesModal
                    onYes={handleUnsavedYes}
                    onNo={handleUnsavedNo}
                />
            )}
        </div>
    );
};

export default HearingRecordDetails;